import React, { Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { SiteProvider } from './context/SiteContext';
import SeoHead from './components/SeoHead';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Rooms from './components/Rooms';
import './App.css';

const Amenities = lazy(() => import('./components/Amenities'));
const TourPackages = lazy(() => import('./components/TourPackages'));
const FestivalOffers = lazy(() => import('./components/FestivalOffers'));
const Gallery = lazy(() => import('./components/Gallery'));
const NearbyAttractions = lazy(() => import('./components/NearbyAttractions'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const GoogleRating = lazy(() => import('./components/GoogleRating'));
const Blog = lazy(() => import('./components/Blog'));
const HowToReach = lazy(() => import('./components/HowToReach'));
const FAQ = lazy(() => import('./components/FAQ'));
const Booking = lazy(() => import('./components/Booking'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));
const WhatsAppButton = lazy(() => import('./components/WhatsAppButton'));
const ScootyRental = lazy(() => import('./components/ScootyRental'));

// Standalone route pages
const ScootyRentalPage = lazy(() => import('./components/ScootyRentalPage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const CancellationPolicy = lazy(() => import('./components/CancellationPolicy'));
const NotFound = lazy(() => import('./components/NotFound'));

// Blog pages (individual URLs for SEO + LLM discoverability)
const BlogPost = lazy(() => import('./components/BlogPost'));

const SectionLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px', color: '#E85D04', fontFamily: 'Poppins, sans-serif', fontSize: '0.9rem' }}>
    Loading...
  </div>
);

// ✅ No duplicate HelmetProvider or SiteProvider here
function HomePage() {
  return (
    <>
      <SeoHead />
      <div className="App">
        <Navbar />
        <Hero />
        <About />
        <Rooms />
        <Suspense fallback={<SectionLoader />}>
          <Amenities />
          <TourPackages />
          <ScootyRental />
          <FestivalOffers />
          <Gallery />
          <NearbyAttractions />
          <Testimonials />
          <GoogleRating />
          <Blog />
          <HowToReach />
          <FAQ />
          <Booking />
          <Contact />
          <Footer />
          <WhatsAppButton />
        </Suspense>
      </div>
    </>
  );
}

// Legal pages get Navbar + Footer wrapped around them
function LegalLayout({ children }) {
  return (
    <>
      <Suspense fallback={<SectionLoader />}>
        <Navbar />
        {children}
        <Footer />
        <WhatsAppButton />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <SiteProvider>
        <Router>
          <Suspense fallback={<SectionLoader />}>
            <Routes>

              {/* ── Homepage ── */}
              <Route path="/" element={<HomePage />} />

              {/* ── Scooty & Bike Rental ── */}
              <Route
                path="/scooty-bike-rental-mathura"
                element={<ScootyRentalPage />}
              />

              {/* ── Legal Pages ── */}
              <Route
                path="/privacy-policy"
                element={
                  <LegalLayout>
                    <PrivacyPolicy />
                  </LegalLayout>
                }
              />
              <Route
                path="/terms-and-conditions"
                element={
                  <LegalLayout>
                    <TermsAndConditions />
                  </LegalLayout>
                }
              />
              <Route
                path="/cancellation-policy"
                element={
                  <LegalLayout>
                    <CancellationPolicy />
                  </LegalLayout>
                }
              />

              {/* Blog / Travel Guide Pages — individual URLs for SEO */}
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* 404 Catch-all */}
              <Route
                path="*"
                element={
                  <LegalLayout>
                    <NotFound />
                  </LegalLayout>
                }
              />

            </Routes>
          </Suspense>
        </Router>
      </SiteProvider>
    </HelmetProvider>
  );
}

export default App;